import Vue from 'vue';
import moment from 'moment';
import VueI18n from 'vue-i18n';

Vue.use(VueI18n);

let i18n = null;

let currentLocale = null;
let localeWeekdays = (new Array(7)).fill('');
let localeWeekdays2 = (new Array(7)).fill('');

function updateWeekdays() {
  currentLocale = i18n.locale;
  const monday = moment('1970-01-05');
  localeWeekdays = localeWeekdays.map(() => {
    const d = monday.format('ddd');
    monday.add(1, 'day');
    return d.capitalize();
  });
  localeWeekdays2 = localeWeekdays2.map(() => {
    const d = monday.format('dd');
    monday.add(1, 'day');
    return d.capitalize();
  });
}

const i18nExtends = {
  format: {
    datetime: (ctx) => {
      let obj = ctx.list(0);
      if (obj == null || !obj) return '';
      const fmt = ctx.list(1);
      if (obj instanceof Date || typeof obj === 'string') {
        obj = moment(obj).locale(moment.locale());
      }
      const format = i18n.te(fmt) ? i18n.t(fmt) : fmt;
      return obj.format(format);
    },
  },
  weekdays() {
    if (currentLocale !== i18n.locale) {
      updateWeekdays();
    }
    return localeWeekdays;
  },
  weekdays2() {
    if (currentLocale !== i18n.locale) {
      updateWeekdays();
    }
    return localeWeekdays2;
  },
};

const extendMessages = (msgs) => ({
  ...msgs,
  ...i18nExtends,
});

/** Locales delivered by the translation API even when no static JSON exists yet (see translation.module). */
const APP_LOCALE_CODES = new Set([
  'en', 'de', 'pt', 'tr', 'ar', 'es', 'fr', 'it', 'nl', 'pl', 'ro', 'ru', 'sl', 'zh', 'da', 'bg',
]);

const loadLocales = () => {
  const context = require.context('./locales', true);
  const messages = context.keys()
    .map((key) => ({ key, locale: key.match(/[-a-z0-9_]+/i)[0] }))
    .reduce((msgs, { key, locale }) => ({
      ...msgs,
      [locale]: extendMessages(context(key)),
    }), {});
  return { context, messages };
};

const { context, messages } = loadLocales();

const getInitialLocale = (availableMessages) => {
  const fallbackLocale = 'en';
  const availableLocales = Object.keys(availableMessages || {});
  const isSupported = (locale) =>
    availableLocales.includes(locale) || APP_LOCALE_CODES.has(locale);

  const browserQuery = typeof window !== 'undefined' ? window.location.search : '';
  const query = new URLSearchParams(browserQuery);
  const hasLocaleQuery = query.has('locale') || query.has('lang');
  const queryLocale = query.get('locale') || query.get('lang');

  if (hasLocaleQuery && typeof window !== 'undefined') {
    query.delete('locale');
    query.delete('lang');
    const nextQuery = query.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  if (queryLocale && isSupported(queryLocale)) {
    localStorage.setItem('locale', queryLocale);
    return queryLocale;
  }

  const storedLocale = localStorage.getItem('locale');
  if (storedLocale && isSupported(storedLocale)) {
    return storedLocale;
  }

  return fallbackLocale;
};

i18n = new VueI18n({
  locale: getInitialLocale(messages),
  fallbackLocale: 'en',
  silentFallbackWarn: true,
  messages,
});

// Map app locale codes to the moment locale filenames shipped under `moment/locale/`.
// English is the built-in default in moment, so no dynamic import is needed.
const MOMENT_LOCALE_MAP = {
  en: null,
  de: 'de',
  pt: 'pt',
  tr: 'tr',
  ar: 'ar',
  es: 'es',
  fr: 'fr',
  it: 'it',
  nl: 'nl',
  pl: 'pl',
  ro: 'ro',
  ru: 'ru',
  sl: 'sl',
  zh: 'zh-cn',
  da: 'da',
  bg: 'bg',
};

const momentLoadPromises = {};

// Sets moment's global locale so that month/weekday tokens (`MMM`, `dddd`, ...) match
// the jQuery UI datepicker's regional pack. This keeps the picker's displayed input value
// (e.g. "12 Mär 2026" in German) round-trippable through `moment(val, "DD MMM YYYY")`.
async function syncMomentLocale(code) {
  const file = MOMENT_LOCALE_MAP[code];
  if (!file) {
    moment.locale('en');
    return;
  }
  if (!momentLoadPromises[file]) {
    momentLoadPromises[file] = import(
      /* webpackChunkName: "moment-locale-[request]" */
      `moment/locale/${file}.js`
    ).catch(() => null);
  }
  try {
    await momentLoadPromises[file];
    moment.locale(file);
  } catch (e) {
    moment.locale('en');
  }
}

syncMomentLocale(i18n.locale);

if (module.hot) {
  module.hot.accept(context.id, () => {
    const { messages: newMessages } = loadLocales();
    Object.keys(newMessages)
      .filter((locale) => messages[locale] !== extendMessages(newMessages[locale]))
      .forEach((locale) => {
        const msgs = extendMessages(newMessages[locale]);
        messages[locale] = msgs;
        i18n.setLocaleMessage(locale, msgs);
        // console.debug(`Changed locale: ${locale}`);
      });
  });
}

/**
 * Deep-merge API strings into existing locale messages and re-apply runtime helpers (format, weekdays).
 * Replaces setLocaleMessage-only updates that wiped extendMessages and broke the UI after language switch.
 */
function applyApiTranslations(translations) {
  Object.keys(translations).forEach((locale) => {
    i18n.mergeLocaleMessage(locale, translations[locale]);
    i18n.mergeLocaleMessage(locale, i18nExtends);
  });
}

export { extendMessages, applyApiTranslations, syncMomentLocale };
export default i18n;
